import { ActionMenuItem } from '@if/mui'
import {
  EBeneficiaryType,
  EContractStatus,
  ECreditApplicationStatus,
  EDecision,
  EFinancingProgram,
  ELoanPurpose,
} from '@if/core/types'
import { useMerchant } from '@src/containers/App/MerchantProvider'
import { CreditApplication, DraftCreditApplicationDto } from '@src/types/CreditApplicationSchema'
import { SelectValueListItem } from '@src/types/SelectValueListItem'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

export const isServerAnalysingCredit = (creditApp: CreditApplication | null): boolean => {
  return (
    creditApp !== null &&
    ((creditApp.consentHardHit && !creditApp.normsCreditDecision) || creditApp.prequalificationDecision == null)
  )
}

export const canPollCreditApp = (creditApp: CreditApplication | null): boolean => {
  if (creditApp) {
    let maxTime = new Date()
    const thirtySeconds = 30 * 1000
    maxTime = new Date(maxTime.getTime() - thirtySeconds)
    const creditAppTime = new Date(creditApp.updatedOn)
    return creditAppTime >= maxTime
  }
  return false
}

// The credit is being analysed server-side and polling is still permitted.
export const isAnalysisActive = (creditApp: CreditApplication | null): boolean =>
  isServerAnalysingCredit(creditApp) && canPollCreditApp(creditApp)

// The pre-qualification hasn't come back yet.
export const hasPendingPrequalCheck = (creditApp: CreditApplication): boolean =>
  creditApp.status === ECreditApplicationStatus.Active && creditApp.prequalificationDecision == null

// The credit checks still owe a result — the norms decision hasn't landed, or the final
// decision hasn't settled while the remaining checks (fraud, etc.) run.
export const hasPendingCreditChecks = (creditApp: CreditApplication): boolean =>
  creditApp.consentHardHit &&
  (creditApp.normsCreditDecision == null || creditApp.finalCreditDecision.decision === EDecision.Pending)

// The application still has async checks running — what the in-progress banner reports. Broader
// than any single step's data: the banner stays up while a later check is still outstanding,
// long after the step's own values have landed and its skeleton is gone.
// Not gated on canPollCreditApp: the checks are outstanding for as long as their results are
// missing, however long that takes, and the page polls to match.
export const hasPendingAsyncChecks = (creditApp: CreditApplication): boolean =>
  hasPendingPrequalCheck(creditApp) || hasPendingCreditChecks(creditApp)

export const isServerCreatingContract = (creditApp: CreditApplication | null): boolean => {
  return (
    creditApp !== null &&
    creditApp.worksheet?.finalDecision.decision === EDecision.Approved &&
    creditApp.contract.status === EContractStatus.NotReady
  )
}

export function adjustCreditApplicationYearsForApi(data: Partial<DraftCreditApplicationDto>) {
  if (data.applicant?.currentAddress && data.applicant.currentAddress.years === 0) {
    data.applicant.currentAddress.months = 11
  }
  if (data.coApplicant?.currentAddress && data.coApplicant.currentAddress.years === 0) {
    data.coApplicant.currentAddress.months = 11
  }
  if (data.applicant && Array.isArray(data.applicant.currentJobs)) {
    data.applicant.currentJobs.forEach((job) => {
      if (job && job.years === 0) {
        job.months = 11
      }
    })
  }
  if (data.coApplicant && Array.isArray(data.coApplicant.currentJobs)) {
    data.coApplicant.currentJobs.forEach((job) => {
      if (job && job.years === 0) {
        job.months = 11
      }
    })
  }
  return data
}
export function adjustCreditApplicationYearsFromApi(data: CreditApplication) {
  if (data.applicant?.currentAddress && data.applicant.currentAddress.years === null) {
    data.applicant.currentAddress.years = 0
  }
  if (data.coApplicant?.currentAddress && data.coApplicant.currentAddress.years === null) {
    data.coApplicant.currentAddress.years = 0
  }
  if (data.applicant && Array.isArray(data.applicant.currentJobs)) {
    data.applicant.currentJobs.forEach((job) => {
      if (job && job.years === null) {
        job.years = 0
      }
    })
  }
  if (data.coApplicant && Array.isArray(data.coApplicant.currentJobs)) {
    data.coApplicant.currentJobs.forEach((job) => {
      if (job && job.years === null) {
        job.years = 0
      }
    })
  }
  return data
}

export const useYearOptions = (length: number) => {
  const { t } = useTranslation()
  return useMemo(() => {
    const maxYear = 15
    const items = [
      <ActionMenuItem key={0} value={0}>
        {t('common.lessThanOneYear')}
      </ActionMenuItem>,
    ]

    for (let year = 1; year <= length; year++) {
      const displayYear = Math.min(year, maxYear)
      items.push(
        <ActionMenuItem key={year} value={displayYear}>
          {year >= maxYear && <>{t('common.moreThan')} </>}
          {displayYear} {t(displayYear === 1 ? 'common.year' : 'common.years')}
        </ActionMenuItem>,
      )
    }

    return items
  }, [length, t])
}
export const useBeneficiaryTypes = (loanPurposeId: ELoanPurpose): SelectValueListItem[] => {
  const { t } = useTranslation()
  const beneficiaryTypesList: SelectValueListItem[] = useMemo(() => {
    const list: SelectValueListItem[] = []
    if (
      loanPurposeId &&
      [ELoanPurpose.Aesthetics, ELoanPurpose.Medical, ELoanPurpose.Dentistry, ELoanPurpose.GoodsAndServices].includes(
        loanPurposeId,
        0,
      )
    ) {
      list.push(
        {
          value: EBeneficiaryType.Applicant,
          label: t(`EBeneficiaryType.${EBeneficiaryType.Applicant}`, { ns: 'enum' }),
        },
        {
          value: EBeneficiaryType.Other,
          label: t(`EBeneficiaryType.${EBeneficiaryType.Other}`, { ns: 'enum' }),
        },
      )
    } else {
      list.push({
        value: EBeneficiaryType.Other,
        label: t(`EBeneficiaryType.${EBeneficiaryType.Other}`, { ns: 'enum' }),
      })
    }
    return list
  }, [loanPurposeId, t])

  return beneficiaryTypesList
}

export const useUserSupportsProgram = (program: EFinancingProgram): boolean => {
  const merchant = useMerchant()
  return merchant.supportedFinancingPrograms.includes(program)
}

export function mustAskApplicantSIN(creditApplication: CreditApplication) {
  return !creditApplication.applicant.hasSin && creditApplication.applicant.isApplicantNewToCredit
}

export function mustAskCoapplicantSIN(creditApplication: CreditApplication) {
  return (
    creditApplication.coApplicant &&
    !creditApplication.coApplicant.hasSin &&
    creditApplication.coApplicant.isApplicantNewToCredit
  )
}
